import { type FC } from "react";
import { type CustomTile, type LinearSubstep, type SubstepTile } from "../../models";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "../../../../redux/store";
import SubstepFrame from "../SubstepFrame";
import LinearTile from "../LinearTile";
import { defaultMemoize } from "reselect";
import { type CustomDispatch } from "../../../../redux/types";
import { updateCsticValuesFromTile } from "../../redux/actions";
import { formatCsticsToSet } from "../../utils";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import classes from "~/Configurators/common/components/GenericTileSubstep/GenericTileSubstep.module.less";
import { TileContainer } from "~/Configurators/common/components/GenericTileSubstep/GenericTileSubstep";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const SystemLayoutSubstep: FC<Props> = ({ def, ...props }) => {
  const dispatch: CustomDispatch<RootState> = useDispatch();
  const systemLayoutHelpTile = useSelector<RootState, CustomTile | null>(
    (state: RootState) => state.linearConfigurator.systemLayoutHelpTile,
  );

  const tileData = getTileData(def, dispatch);

  const debugLanguage = isDebugLanguageEnabled();

  const tiles = tileData.map((tile, index) => (
    <LinearTile
      key={index}
      index={null}
      onClickHandler={tile.onClick}
      imageUrl={tile.imageUrl}
      title={debugLanguage ? formatCsticsToSet(tile.cstics) : tile.name}
      info={tile.info}
      isPicked={tile.tileIndex === def.selectedTile}
    />
  ));
  if (systemLayoutHelpTile) {
    tiles.splice(def.tilesPerRow - 1, 0, <div key={"emptySlot"} />);
    tiles.push(
      <LinearTile
        key={"helpTile"}
        selectable={false}
        index={null}
        imageUrl={def.extraTexts["helpTileImageUrl"]}
        title={def.extraTexts["helpTileName"]}
        openLinkInNewTab={true}
        link={def.extraTexts["helpTileUrl"]}
      />,
    );
  }

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={def.selectedTile !== null}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <TileContainer columnAmount={def.tilesPerRow} className={classes.content}>
        {tiles}
      </TileContainer>
    </SubstepFrame>
  );
};
export default SystemLayoutSubstep;

type Tile = SubstepTile & {
  onClick: () => void;
  tileIndex: number;
};

const getTileData = defaultMemoize((stepElement: LinearSubstep, dispatch: CustomDispatch<RootState>) => {
  const allTiles: Tile[] = stepElement.tiles.map((tile, index) => ({
    ...tile,
    onClick: () => dispatch(updateCsticValuesFromTile(stepElement.type, index, tile)),
    tileIndex: index,
  }));

  return allTiles;
});
